<template>
<div>
  <el-table-wrapper border :data="tableData" :columns="columns" :show-custom-header="true" :show-search-tool="showSearchTool" ref="table" style="width:100%;"
  :show-overflow-tooltip="true" :span-method="objectSpanMethod" @row-click="onClickRow" :pagination="false" class="calendar-list"
  :remote="true" :column-default="columnDefault" @condition-change="onChangeTable">
    <template slot-scope="scope" slot="add-slot">
      <el-button @click.stop="onClickCell(scope.row)" circle size="mini"><i class="fas fa-plus"></i></el-button>
    </template>
    <template slot-scope="scope" slot="work-impact-slot">
      <span :class="$style['grade-a']" v-if="scope.row.workImpact=='GRADE_A'">매우 높음</span>
      <span :class="$style['grade-b']" v-else-if="scope.row.workImpact=='GRADE_B'">높음</span>
      <span :class="$style['grade-c']" v-else-if="scope.row.workImpact=='GRADE_C'">중간</span>
      <span :class="$style['grade-d']" v-else-if="scope.row.workImpact=='GRADE_D'">낮음</span>
      <span v-else-if="scope.row.endDatetime == null" >{{ EMPTY_TEXT }}</span>
      <span :class="$style['grade-null']" v-else>영향도 없음</span>
    </template>
    <template slot-scope="scope" slot="objname-slot">
      <span v-if="scope.row.objCount == 1" >
        {{ scope.row.objName }}
      </span>
      <span v-else-if="scope.row.objCount != null" >
        {{ scope.row.objName }} 외 {{ scope.row.objCount - 1 }} 건
      </span>
      <span v-else> {{ EMPTY_TEXT }} </span>
    </template>
    <template slot-scope="scope" slot="date-slot">
      <span :class="$style.sunday" v-if="getLastText(getDateConvert(scope.row.startDatetime), '일')" v-text="getDateConvert(scope.row.startDatetime)"></span>
      <span :class="$style.saturday" v-else-if="getLastText(getDateConvert(scope.row.startDatetime), '토')" v-text="getDateConvert(scope.row.startDatetime)"></span>
      <span v-else v-text="getDateConvert(scope.row.startDatetime)"></span>
    </template>
  </el-table-wrapper>
</div>
</template>

<script>
import moment from 'moment';
import { isNil, differenceBy, concat, sortBy, remove, isEmpty } from 'lodash';
import { columnDefault } from '@/constants/table';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import workManageAPI from '@/api/work/work';

const workStatusFilters = [{ text: '작업 등록', value: 'A' }, { text: '처리 실패', value: 'F' }, { text: '처리 중', value: 'P' }, { text: '처리 성공', value: 'S' }];
const workImpactFilters = [{ text: '매우 높음', value: 'GRADE_A' }, { text: '높음', value: 'GRADE_B' }, { text: '중간', value: 'GRADE_C' }, { text: '낮음', value: 'GRADE_D' }, { text: '영향도 없음', value: null }];
const workTypeIdFilters = [{ value: 'CHANGE', text: '변경 작업' }, { value: 'CHECK', text: '점검 작업' }, { value: 'CONSTRUCT', text: '공사 작업' }];
export default {
  name: 'WWorkListTable',
  props: {
    selectedDate: Date,
    filteringConfig: Object,
  },
  data() {
    return {
      columns: [
        { prop: 'startDatetime', headerAlign: 'center', label: '일자', scopedSlot: 'date-slot', width: 70 },
        { label: '추가', headerAlign: 'center', align: 'center', scopedSlot: 'add-slot', width: 60 },
        { prop: 'workTypeId', headerAlign: 'center', label: '작업 구분', formatter: this.getWorkTypeAndStatusFormatter, filterMultiple: false, filters: workTypeIdFilters },
        { prop: 'workImpact', headerAlign: 'center', label: '영향도', scopedSlot: 'work-impact-slot', filterMultiple: false, filters: workImpactFilters },
        { prop: 'workName', headerAlign: 'center', label: '작업명', searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'workContents', headerAlign: 'center', label: '작업내용', searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'objName', headerAlign: 'center', remoteProp: 'w.objNameData', label: '대상장비', searchable: true, scopedSlot: 'objname-slot' },
        { prop: 'managerId', headerAlign: 'center', label: '작업자', searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'workStatus', headerAlign: 'center', label: '작업 상태', formatter: this.getWorkTypeAndStatusFormatter, filterMultiple: false, filters: workStatusFilters },
        { prop: 'startDatetime', headerAlign: 'center', label: '시작시간', searchable: true, searchType: 'date', formatter: this.getDateFormatter },
        { prop: 'endDatetime', headerAlign: 'center', label: '종료시간', searchable: true, searchType: 'date', formatter: this.getDateFormatter },
      ],
      columnDefault,
      showSearchTool: false,
      tableData: [],
      originTableData: [],
      EMPTY_TEXT,
    };
  },
  watch: {
    /**
     * [selectedDate date-picker 클릭시]
     * @type {Yang}
     */
    selectedDate: {
      handler(newValue, oldValue) {
        if (!oldValue || newValue.getFullYear() !== oldValue.getFullYear() || newValue.getMonth() !== oldValue.getMonth()) {
          if (this.$refs.table === undefined) {
            this.$nextTick(() => {
              this.loadStore();
            });
          } else {
            this.loadStore();
          }
        }
      },
      immediate: true,
    },
    /**
     * [filteringConfig 작업 별 필터링]
     * @type {Yang}
     */
    filteringConfig: {
      handler() {
        const dataArray = [];
        this.originTableData.forEach((item) => { // 등록된 작업 추출
          if (!isNil(item.endDatetime)) {
            dataArray.push(item);
          }
        });
        const workImpact = this.filteringConfig.workImpact;
        const workSchedule = this.filteringConfig.workSchedule;
        const workType = this.filteringConfig.workType;
        const filterArray = dataArray.filter((work) => { // 필터링 된 row 추출
          if (workSchedule.indexOf(work.scheduleType) > -1 && workType.indexOf(work.workTypeId) > -1) {
            if (isEmpty(work.workImpact)) {
              return workImpact.indexOf('GRADE_NULL') > -1;
            }
            return workImpact.indexOf(work.workImpact) > -1;
          }
          return false;
        });
        const otherArray = differenceBy(dataArray, filterArray, ['workSeqNumber', 'startDatetime', 'endDatetime']);
        this.tableData = differenceBy(this.originTableData, otherArray, ['workSeqNumber', 'startDatetime', 'endDatetime']); // 데이터 필터링 후 테이블에 데이터 바인딩
        const startDates = [];
        otherArray.forEach((item) => { // 필터링된 데이터 row 생성
          startDates.push({ startDatetime: item.startDatetime, workSeqNumber: item.workSeqNumber });
        });
        this.tableData = sortBy(concat(this.tableData, startDates), ['startDatetime']); // 필터링된 row에 새로운 row 삽입 후 정렬
        startDates.forEach((stItem) => { // 생성된 row가 테이블에 데이터가 있으면 삭제
          let count = 0;
          this.tableData.forEach((item) => {
            if (getDateToString(item.startDatetime, 'DD') === getDateToString(stItem.startDatetime, 'DD')) {
              count += 1;
            }
          });
          if (count > 1) {
            remove(this.tableData, (rmItem) => rmItem.workSeqNumber === stItem.workSeqNumber);
          }
        });
      },
      deep: true,
    },
  },
  methods: {
    /**
     * 테이블 데이터 빈값 확인
     * @author Yang
     */
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * [objectSpanMethod 병합 메서드]
     * @author {Yang}
     * @param  {[type]} row         [description]
     * @param  {[type]} columnIndex [description]
     * @param  {[type]} }           [description]
     * @return {[type]}             [description]
     */
    objectSpanMethod({ row, rowIndex, columnIndex }) {
      if (columnIndex === 0) {
        let count = 0;
        let firstFlag = true;
        for (let i = this.tableData.length - 1; i >= 0; i -= 1) {
          if (getDateToString(row.startDatetime, 'DD') === getDateToString(this.tableData[i].startDatetime, 'DD')) {
            if (i >= rowIndex) {
              count += 1;
            } else {
              firstFlag = false;
            }
          }
        }
        if (firstFlag && count === 1) {
          return [1, 1];
        }
        if (!firstFlag) {
          return [0, 0];
        }
        return [count, 1];
      }
      return [1, 1];
    },
    /**
     * [onClickSetSearchToolVisible 검색 토글]
     * @author {Yang}
     */
    onClickSetSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    /**
     * [getDateFormatter date formatter]
     * @author {Yang}
     */
    getDateFormatter(row, column, value) {
      return isNil(row.endDatetime) ? EMPTY_TEXT : getDateToString(value);
    },
    /**
     * [loadStore 작업내역 (목록) 리스트 가져오기]
     * @author {Yang}
     */
    loadStore() {
      const tableParams = this.$refs.table.getParameter();
      const params = {
        selectedDate: moment(new Date(getDateToString(this.selectedDate, 'YYYY-MM-DD'))).hours(0).minute(0).second(0),
        ...tableParams,
      };
      workManageAPI.getWorkListForTable(params)
        .then((result) => {
          if (result.data.success === true) {
            this.originTableData = result.data.data;
            this.tableData = this.originTableData;
          }
        });
    },
    /**
     * [getDateConvert 일자 포멧터]
     * @author {Yang}
     */
    getDateConvert(value) {
      return getDateToString(value, 'MM.DD dd');
    },
    /**
     * [getLastText 요일대로 컬러 입히기.]
     * @author {Yang}
     */
    getLastText(value, str) {
      return value.charAt(value.length - 1) === str;
    },
    onClickCell(row) {
      this.$emit('add-column-click', row.startDatetime);
    },
    /**
     * [onClickRow 테이블 row 클릭]
     * @author {Yang}
     */
    onClickRow(row) {
      if (!isNil(row.workSeqNumber) && !isNil(row.workName)) {
        this.$emit('workClick', row);
      }
    },
    /**
     * [getWorkTypeAndStatusFormatter 작업구분, 작업상태 포멧터]
     * @author {Yang}
     */
    getWorkTypeAndStatusFormatter(row, column, value) {
      switch (value) {
        case 'CHANGE': return '변경 작업';
        case 'CHECK': return '점검 작업';
        case 'CONSTRUCT': return '공사 작업';
        case 'A': return '작업 등록';
        case 'F': return '처리 실패';
        case 'P': return '처리 중';
        case 'S': return '처리 성공';
        default: return EMPTY_TEXT;
      }
    },
    onChangeTable() {
      this.loadStore();
    },
  },
};
</script>
<style module>
.sunday {color: #f96464;}
.saturday {color: #1c53ca;}
.grade-a {color: #ef6a6a;}
.grade-b {color:#f1aa61;}
.grade-c {color:#6dca7d;}
.grade-d {color:#69a3d2;}
.grade-null {color: #ddd;}
</style>
<style lang="scss">
// module 내 edit 1115
//.sunday {
//   color: #f96464;
// }
// .saturday {
//   color: #1c53ca;
// }
// .grade-a {
//   color: #f43248;
// }
// .grade-b {
//   color:#fb8637;
// }
// .grade-c {
//   color:#2c9840;
// }
// .grade-d {
//   color:#5a9ee4;
// }
// .grade-null {
//   color: #bbb;
// }
.calendar-list{
  .el-table--border, .el-table--group{
    border: 0;
    border-top: 1px solid #888; //$chinagray
  }
  // .el-table th{
  //   height: 36px;
  //   background-color: transparent;
  // }
  .el-table td{
    //height: 40px;
    padding: 5px 0;
    cursor: pointer;
    background-color: transparent;
  }
  .el-table--border::after, .el-table--group::after, .el-table::before{
    background-color: transparent;
  }
  .el-table td .cell, .el-table th .cell{
    padding-top: 0;

    .el-button.is-circle{
      width: 24px;
      height: 24px;
      padding: 0;
      line-height: initial;
      display: inline-flex;
      justify-content: center;
      align-self: center;
      font-size: 12px;
      background-color: #f7f7f7;
      color: #888; //$chinagray

      & > span{
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
  .el-table td, .el-table th{
    vertical-align: middle;
    border: 0 !important;
    border-bottom: 1px solid #ccc !important;
  }
  .el-table--border th:first-child .cell{
    padding-left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/work/components/WorkListTable.vue